
import { useTranslation } from "react-i18next";
import NewPage from "../../../layouts/page/NewPage";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import MenuConfig from "../../../../constants/MenuConfig";
import Form from "./Form";
import { getId, sendToApi } from "../../../../utils/ApiUtils";
import Api from "../../../../constants/Api";
import { convertBooleanToStatus, convertStringToStringDouble } from "../../../../utils/ConverterUtils";
import { useNavigate } from "react-router-dom";
import { isNumber } from "../../../../utils/CheckUtils";
import { successAlert, warnAlert, warnAlertYesOrNo } from "../../../components/alert/defaultAlert";
import { ApiError } from "../../../components/Common/ApiError";
import axiosInstance from "../../../../services/AxiosInstance";
import PaymentProvider from "../../../../constants/PaymentProvider";
import { ALL_WORK_DAYS, sortWorkDays } from "../../../../utils/WorkDayUtils";

const PlanNew = () => {

    const { t } = useTranslation();
    const defaultPeriodicity = {
        value: 'MONTHLY',
        label: t('monthly')
    };
    const [entityData, setEntityData] = useState(null);
    const [resetForm, setResetForm] = useState(false);
    const [status, setStatus] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [currentProvider, setCurrentProvider] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const loadCurrentProvider = async () => {
            try {
                // Resolve primeiro a empresa autenticada, sem depender de
                // localStorage nem assumir um tenant padrão.
                const companyResponse = await axiosInstance.get(Api.registration.company_info);
                const tenantId = companyResponse?.data?.id;
                if (!tenantId) {
                    throw new Error('Empresa autenticada sem identificador.');
                }
                const response = await axiosInstance.get(`${Api.integration.payment}/provider/${tenantId}`);
                setCurrentProvider(response.data);
            } catch (error) {
                setCurrentProvider(null);
            }
        };

        loadCurrentProvider();
    }, []);

    // Define o schema de validação do form.
    const formSchema = Yup.object().shape({
        name: Yup.string().required(t('validation.required.name')),
        periodicity: Yup.object().nullable().required(t('validation.required.periodicity')),
        price: Yup.string().required(t('validation.required.price')),
        qtyTransactions: Yup.string().required(t('validation.required.qty_transactions')),
        // A duração em dias só é cobrada quando o pacote em dias está ligado: de 1 a 365.
        packageDays: Yup.string().when('dailyPackage', {
            is: true,
            then: schema => schema.test('package-days', 'Informe uma duração de 1 a 365 dias', value => {
                const days = Number(value);
                return Number.isInteger(days) && days >= 1 && days <= 365;
            }),
            otherwise: schema => schema.optional()
        }),
        // Produtos deixou de ser obrigatório: a mesma tela edita o plano existente, e exigir
        // pelo menos um serviço barrava operações que nada têm a ver com a lista - inativar
        // um plano antigo, por exemplo, ficava impossível quando ele não tinha produtos.
        // Zero dias marcados deixaria o plano sem nenhum dia de uso no site. O cadastro novo
        // começa com a semana toda selecionada; na edição a exigência sai: plano antigo pode
        // ter nascido sem dias marcados, e cobrar isso agora travava qualquer alteração nele,
        // inclusive a inativação, que é justamente quando os dias deixam de importar.
        benefitDays: entityData?.id
            ? Yup.array()
            : Yup.array().min(1, 'Selecione pelo menos um dia de uso do plano.'),
    });

    // Define o formik.
    const formik = useFormik({
        initialValues: {
            name: '',
            price: '',
            products: [],
            planProducts: [],
            qtyTransactions: 0,
            // Pacote com duração em dias (só na loja). Desligado é o plano comum, pela periodicidade.
            dailyPackage: false,
            packageDays: '',
            maxAppointments: '',
            // Vazio = segue o limite geral de Configurações > Agendamento.
            maxFutureAppointments: '',
            // Vazio = segue os dias gerais do calendário de Configurações > Agendamento.
            publicBookingDays: '',
            // Um plano novo funciona a semana toda por padrão, permitindo salvar com os campos
            // essenciais; o usuário pode restringir os dias depois no próprio formulário.
            benefitDays: sortWorkDays(ALL_WORK_DAYS),
            offDayDiscountPercentage: '',
            periodicity: defaultPeriodicity
        },
        validationSchema: formSchema,
        onSubmit: values => {
            if (!isNumber(values.qtyTransactions)) {
                warnAlert(t('validation.invalid.qty_transactions_only_numbers'));
                formik.setSubmitting(false); // Habilita o botão submit
                formik.setFieldValue('qtyTransactions', 0);
                return;
            }

            // Produtos e Descontos
            let planProductsPayload = [];
            if (values.planProducts) {
                values.planProducts.forEach((item) => {
                    planProductsPayload.push({
                        productId: item.productId,
                        discountPercentage: item.discountPercentage
                    });
                });
            }

            let payload = {
                id: getId(entityData),
                name: values.name,
                periodicity: values.periodicity.value,
                price: convertStringToStringDouble(values.price),
                status: convertBooleanToStatus(status),
                // Pacote em dias não tem ciclos de cobrança: zero é "indeterminado" para o backend.
                qtyTransactions: values.dailyPackage ? 0 : values.qtyTransactions,
                packageDays: values.dailyPackage ? Number(values.packageDays) : null,
                maxAppointments: values.maxAppointments ? Number(values.maxAppointments) : null,
                // null mantém o plano seguindo o limite geral; o backend também trata zero assim.
                maxFutureAppointments: values.maxFutureAppointments ? Number(values.maxFutureAppointments) : null,
                // Mesma leitura: null segue o geral; o backend também limita ao teto de 90 dias.
                publicBookingDays: values.publicBookingDays ? Number(values.publicBookingDays) : null,
                benefitDays: sortWorkDays(values.benefitDays),
                offDayDiscountPercentage: values.offDayDiscountPercentage
                    ? Number(convertStringToStringDouble(values.offDayDiscountPercentage))
                    : null,
                integrationId: entityData?.integrationId ?? null,
                planProducts: planProductsPayload
            }

            // Aciona a api
            sendToApi(Api.subscription.plan, payload, () => {
                if (entityData == null) {
                    onReset();
                } else {
                    navigate(MenuConfig.subscription.plan.route);
                }
            }, (error) => {
                ApiError(error);
                formik.setSubmitting(false);
            });
        },
    });

    // Valida antes de chamar o submit do Formik para explicar no alerta por que o envio foi
    // bloqueado; sem isso, erros não tocados ficam discretos nos campos e parecem clique sem efeito.
    const onSubmit = async (e) => {
        e.preventDefault();
        const errors = await formik.validateForm();
        const messages = Object.values(errors).flatMap((error) => {
            if (typeof error === 'string') return [error];
            if (Array.isArray(error)) return error.filter((item) => typeof item === 'string');
            if (error && typeof error === 'object') {
                return Object.values(error).filter((item) => typeof item === 'string');
            }
            return [];
        });

        if (messages.length > 0) {
            formik.setTouched(Object.fromEntries(Object.keys(errors).map((field) => [field, true])));
            warnAlert(messages.join('\n'));
            return;
        }

        // Desativar plano com assinantes pede confirmação: ele sai das vendas novas, mas quem já
        // assina continua nele. Sem o aviso, a empresa desativava o plano, criava outro com o mesmo
        // nome e os assinantes ficavam no antigo, como no tenant 53.
        const openSubscriptions = Number(entityData?.openSubscriptionCount || 0);
        const deactivating = entityData?.status === 'ACTIVE' && !status;
        if (deactivating && openSubscriptions > 0) {
            warnAlertYesOrNo(t('plan_deactivate_confirm', { count: openSubscriptions }), () => formik.handleSubmit());
            return;
        }

        formik.handleSubmit();
    }

    const onReset = () => {
        formik.resetForm({
            values: {
                ...formik.initialValues,
                products: [],
                planProducts: [],
                qtyTransactions: 0,
                maxAppointments: '',
                maxFutureAppointments: '',
                publicBookingDays: '',
                benefitDays: sortWorkDays(ALL_WORK_DAYS),
                offDayDiscountPercentage: '',
                periodicity: defaultPeriodicity
            }
        });
        setStatus(true);

        // Limpa o campo número de transações
        setResetForm(!resetForm);
    }

    const onSync = () => {
        if (!entityData?.id) return;
        setSyncing(true);
        axiosInstance.post(`${Api.subscription.plan}/${entityData.id}/sync`)
            .then(() => {
                successAlert(t('plan_sync_success'));
            })
            .catch((error) => {
                ApiError(error);
            })
            .finally(() => {
                setSyncing(false);
            });
    }

    return (
        <>
            <NewPage
                motherMenu={t('subscriptions')}
                childMenu={t('plan')}
                activeMenu={t('new')}
                child={
                    <Form
                        formik={formik}
                        entityData={entityData}
                        resetForm={resetForm}
                        status={status} setStatus={setStatus}
                        currentProvider={currentProvider}
                    />}
                setEntityData={setEntityData}
                path={Api.subscription.plan}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={MenuConfig.subscription.plan.route}
                customButtons={
                    entityData?.id && currentProvider !== PaymentProvider.ASAAS ? (
                        <button
                            type="button"
                            className="btn btn-warning text-white fw-medium ms-2"
                            onClick={onSync}
                            disabled={syncing}
                            title={t('plan_sync_hint')}
                        >
                            <i className="bi bi-arrow-repeat me-1"></i>
                            {syncing ? t('syncing') : t('plan_sync_provider')}
                        </button>
                    ) : null
                }
            />
        </>
    );
}

export default PlanNew;
